import { Divider, Pill, Tooltip } from '@mantine/core';
import { FC, ReactElement } from 'react';

import CircularLoader from '@/components/CircularLoader.tsx';
import InstanceIndicator from '@/components/InstanceIndicator.tsx';
import PatientRecordLink from '@/components/PatientRecordLink.tsx';
import PatientTypeIndicator from '@/components/PatientTypeIndicator.tsx';
import StatusLamp from '@/components/StatusLamp.tsx';
import Paragraph from '@/components/text/Paragraph.tsx';

import useConfig from '@/hooks/useConfig.ts';
import { useEnrichedPractitionerDataForSpecificInstance } from '@/hooks/useEnrichedPractitionerData.ts';

import { FuseResultMatch } from '@/models/Fuse.ts';
import { IndicatorStatus } from '@/models/IndicatorStatus.ts';
import { NormalizedPatient, PatientCardType, PatientInsurance } from '@/models/Patient.ts';
import { Insurance } from '@/models/appointment/Appointment.ts';
import { PVSProvider } from '@/models/customers/PVSProvider.ts';
import { EnrichedDataCurUser } from '@/models/data-cur/DataCurUser.ts';

import { dayjs } from '@/utils/dayjsSetup.ts';
import { getInsuranceStatus, getInsuranceTypeDisplay } from '@/utils/insurance-utils.ts';
import { getPatientIdFromUser } from '@/utils/patientUtils.ts';
import { cn } from '@/utils/styleUtils.ts';

interface Props {
    patient: NormalizedPatient | EnrichedDataCurUser;
    matches: FuseResultMatch[];
    patientCardType?: PatientCardType;
}

const PatientRow: FC<Props> = ({ patient, matches, patientCardType = PatientCardType.SEARCH_RESULT }) => {
    const { data: enriched_prac_data, is_loading: is_prac_data_loading } =
        useEnrichedPractitionerDataForSpecificInstance(patient.instance_id);

    const { getInstanceConfig } = useConfig();
    const config = getInstanceConfig(patient.instance_id);
    const is_card_read_available = config?.is_card_read_available;

    const is_doc_cirrus_instance = config?.pvs_provider === PVSProvider.DOC_CIRRUS;

    const dc_patient = patient as NormalizedPatient;
    const data_cur_patient = patient as EnrichedDataCurUser;

    const formatted_date = dayjs(
        is_doc_cirrus_instance ? dc_patient.account.date_of_birth : data_cur_patient.birth_date
    ).format('DD.MM.YYYY');

    const buildHighlightFlags = (): {
        is_first_name_highlighted: boolean;
        is_last_name_highlighted: boolean;
        is_dob_highlighted: boolean;
        is_patient_number_highlighted: boolean;
        is_email_highlighted: boolean;
    } => {
        if (is_doc_cirrus_instance && patientCardType === PatientCardType.SEARCH_RESULT) {
            return {
                is_first_name_highlighted: matches.some(
                    (match: FuseResultMatch) => match.key === '_normalized.first_name'
                ),
                is_last_name_highlighted: matches.some(
                    (match: FuseResultMatch) => match.key === '_normalized.last_name'
                ),
                is_dob_highlighted: matches.some((match: FuseResultMatch) => match.key === '_normalized.date_of_birth'),
                is_patient_number_highlighted: matches.some(
                    (match: FuseResultMatch) => match.key === '_normalized.patient_number'
                ),
                is_email_highlighted: matches.some(
                    (match: FuseResultMatch) => match.key === '_normalized.primary_email'
                ),
            };
        }

        return {
            is_dob_highlighted: false,
            is_email_highlighted: false,
            is_first_name_highlighted: false,
            is_last_name_highlighted: false,
            is_patient_number_highlighted: false,
        };
    };

    const {
        is_first_name_highlighted,
        is_last_name_highlighted,
        is_dob_highlighted,
        is_patient_number_highlighted,
        is_email_highlighted,
    } = buildHighlightFlags();

    const renderInsurance = (insurance: PatientInsurance, cost_center: string | undefined): ReactElement => {
        const status: IndicatorStatus = getInsuranceStatus(insurance);
        const insurance_name: string | undefined = insurance.insurance_print_name;

        let tooltip_text = `${getInsuranceTypeDisplay(insurance.type)} ${insurance_name ? `- ${insurance_name}` : ''}`;

        if (getInsuranceTypeDisplay(insurance.type) === Insurance.PUBLIC) {
            switch (status) {
                case IndicatorStatus.ACTIVE: {
                    tooltip_text += 'Aktiv letzter kartenzugriff';
                    break;
                }
                case IndicatorStatus.INACTIVE: {
                    tooltip_text += 'Inaktiv letzter kartenzugriff';
                    break;
                }
                default: {
                    tooltip_text += 'Unbekannt letzter kartenzugriff';
                    break;
                }
            }

            tooltip_text += ` ${dayjs(insurance.card_swipe_date).format('DD.MM.YYYY')}`;
        }

        if (!cost_center) return <></>;

        return (
            <Tooltip key={`${insurance?.id}-${cost_center}`} label={tooltip_text} position="top-start">
                <div className="flex items-center gap-1">
                    {is_card_read_available && <StatusLamp status={status} text="" />}
                    <Paragraph className="overflow-hidden text-ellipsis whitespace-nowrap">{cost_center}</Paragraph>
                </div>
            </Tooltip>
        );
    };

    const buildInsurancesComponent = (): ReactElement[] | ReactElement | undefined => {
        if (is_prac_data_loading) {
            return <CircularLoader />;
        }

        if (!is_doc_cirrus_instance) {
            return undefined;
        }

        const enriched_insurances: {
            insurance: PatientInsurance;
            cost_center: string | undefined;
        }[] =
            dc_patient.insurances
                ?.map((insurance) => {
                    const cost_center: string | undefined = enriched_prac_data?.practices?.find(
                        (p) => p.pvs?.id === insurance.location_id
                    )?.pvs?.name;

                    return {
                        insurance,
                        cost_center,
                    };
                })
                .filter((item) => item.cost_center !== undefined)
                .sort((a, b) => {
                    return a.cost_center!.localeCompare(b.cost_center!);
                }) ?? [];

        const insurance_items: ReactElement[] = enriched_insurances.map(({ insurance, cost_center }) =>
            renderInsurance(insurance, cost_center)
        );

        return insurance_items;
    };

    const insurances_component = buildInsurancesComponent();

    return (
        <div className={cn('flex flex-col', is_card_read_available ? 'gap-5' : 'gap-1')}>
            <InstanceIndicator instanceId={patient.instance_id} />
            <div className="flex flex-row">
                <div className="w-1/4">
                    <Paragraph className="w-fit">
                        <span className={cn('p-0.5', is_last_name_highlighted && 'bg-amber-100')}>
                            {patient.name.last_name}
                        </span>
                        <span className={cn('p-0.5', is_first_name_highlighted && 'bg-amber-100')}>
                            {patient.name.first_name}
                        </span>
                    </Paragraph>
                </div>
                <div className="ml-2 flex w-2/4 flex-col">{insurances_component}</div>
                <div className="flex w-1/4 justify-end">
                    <Paragraph className="w-fit">
                        <span className={cn('p-0.5', is_dob_highlighted && 'bg-amber-100')}>{formatted_date}</span>
                    </Paragraph>
                </div>
            </div>
            <div className="flex flex-col gap-2">
                <Divider />
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Pill className={cn('text-xs', is_patient_number_highlighted && 'bg-amber-100')}>
                            {is_doc_cirrus_instance ? dc_patient.account.patient_number : data_cur_patient.number}
                        </Pill>
                        <PatientRecordLink
                            pvsPatientId={getPatientIdFromUser(patient) || ''}
                            instanceId={patient.instance_id}
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        {((is_doc_cirrus_instance && dc_patient.primary_email) ||
                            (!is_doc_cirrus_instance && data_cur_patient.contact.email)) && (
                            <Pill className={cn('text-xs', is_email_highlighted && 'bg-amber-100')}>
                                {is_doc_cirrus_instance ? dc_patient.primary_email : data_cur_patient.contact.email}
                            </Pill>
                        )}
                        <PatientTypeIndicator isRegistered={patient.is_registered ?? false} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PatientRow;
